import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { HiCheckBadge } from 'react-icons/hi2';
import { FiLoader } from 'react-icons/fi';

const GOOGLE_SHEET_WEBAPP_URL =
  'https://script.google.com/macros/s/AKfycbytYmuob7_TKektRvtwzUUjid5E8o9WEvXEZaa_N31t2yLCH7fJRbHidOpnZU3bZko1/exec';

const BACKGROUND_IMAGE = 'afdasthumb.jpeg';
const TEACHER_IMAGE = 'logo.png';

export default function App() {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [windowDim, setWindowDim] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowDim({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredVal = value;

    if (name === 'name') {
      filteredVal = value.replace(/[^A-Za-z ]/g, ''); // only letters/spaces
    } else if (name === 'phone') {
      filteredVal = value.replace(/[^0-9]/g, '').slice(0, 10); // max 10 digits
    }

    setFormData((prev) => ({ ...prev, [name]: filteredVal }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    else if (formData.phone.length !== 10)
      newErrors.phone = 'Must be 10 digits';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      await fetch(GOOGLE_SHEET_WEBAPP_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
    } catch (err) {
      alert('Error submitting. Please check your network.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="relative min-h-screen flex flex-col items-center justify-center bg-center bg-cover"
        style={{ backgroundImage: `url('${BACKGROUND_IMAGE}')` }}
      >
        <Confetti
          width={windowDim.width}
          height={windowDim.height}
          numberOfPieces={220}
          recycle={true}
        />

        <div className="z-10 backdrop-blur-xl bg-white/80 rounded-3xl shadow-2xl py-14 px-6 sm:px-12 text-center border-2 border-violet-200 max-w-md mx-auto">
          <div className="flex justify-center mb-2">
            <span className="animate-bounce text-5xl">🎉</span>
          </div>

          <div className="flex justify-center mb-3">
            <HiCheckBadge className="text-violet-700 drop-shadow-lg text-6xl" />
          </div>

          <h2 className="text-5xl font-extrabold mb-3 bg-gradient-to-r from-violet-700 via-pink-600 to-orange-500 bg-clip-text text-transparent">
            Thank You!
          </h2>

          <p className="text-xl my-2 font-semibold text-blue-900">
            Your submission was received
          </p>

          <div className="flex flex-col gap-4 mt-10 justify-center">
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href =
                  'https://docs.google.com/document/d/1JwzsU1DNy_C-hbJLNR-nEt12wvnqpkbN/edit?usp=drive_link&ouid=115172554032019716502&rtpof=true&sd=true';
                link.download = 'CUET_Notes.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
            >
              Download Free Resource
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex justify-center items-center bg-center bg-no-repeat bg-cover px-2 md:px-0"
      style={{
        backgroundImage: `url('${BACKGROUND_IMAGE}')`,
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="relative w-full max-w-lg mx-auto p-0 rounded-3xl shadow-lg bg-white/85 backdrop-blur-xl border border-violet-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 bg-gradient-to-r from-blue-200 via-violet-200 to-pink-100 px-6 py-3 mb-2">
          <img
            src={TEACHER_IMAGE}
            alt="Teacher"
            className="w-12 h-12 rounded-full border-2 border-white shadow-lg object-cover"
          />
          <div>
            <span className="block text-l text-blue-700 font-bold">
              JR Classes
            </span>
            <span className="block text-xs text-gray-500">
              By: Jatin Rajpal
            </span>
          </div>
        </div>

        {/* Title */}
        <div className="flex flex-col items-center pt-4 pb-2 px-8">
          <h1 className="font-extrabold text-3xl md:text-4xl mb-1 text-center bg-gradient-to-r from-blue-700 via-pink-400 to-fuchsia-600 bg-clip-text text-transparent">
            Get Your Free Resource
          </h1>
          <p className="text-center text-gray-700 text-base mb-2">
            Fill in your details below to receive your free Resource.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          autoComplete="off"
          className="space-y-8 w-full px-8 pb-10"
        >
          {[
            { id: 'name', label: 'Name', type: 'text', placeholder: ' ' },
            {
              id: 'phone',
              label: 'Phone Number',
              type: 'text',
              placeholder: ' ',
            },
          ].map((f) => (
            <div className="relative mt-7" key={f.id}>
              <input
                type={f.type}
                id={f.id}
                name={f.id}
                required
                value={formData[f.id]}
                onChange={handleChange}
                className={`block px-4 pb-2.5 pt-5 w-full text-base text-gray-800 bg-white/70 border rounded-2xl shadow focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 peer placeholder-transparent transition ${
                  errors[f.id]
                    ? 'border-red-500 focus:border-red-600 focus:ring-red-100'
                    : 'border-gray-300 focus:border-blue-600 focus:ring-blue-100'
                }`}
                placeholder={f.placeholder}
                maxLength={f.id === 'phone' ? 10 : undefined}
              />
              <label
                htmlFor={f.id}
                className="absolute left-4 top-1.5 z-10 text-base text-gray-500 bg-white/70 px-1 transition-all duration-300 transform -translate-y-1.5 scale-90 origin-[0] peer-focus:left-4 peer-focus:-translate-y-4 peer-focus:scale-90 peer-focus:text-blue-700 peer-placeholder-shown:top-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-gray-500"
              >
                {f.label}
              </label>
              {errors[f.id] && (
                <p className="text-red-500 text-xs mt-1 ml-1">{errors[f.id]}</p>
              )}
            </div>
          ))}

          <button
            disabled={loading}
            type="submit"
            className={`w-full py-3 mt-8 rounded-xl font-bold tracking-wide flex items-center justify-center text-lg transition-all duration-200 ${
              loading
                ? 'bg-blue-400 text-blue-100 cursor-wait'
                : 'bg-gradient-to-r from-blue-600 via-indigo-500 to-pink-500 hover:from-blue-700 hover:to-fuchsia-600 text-white shadow-lg'
            }`}
          >
            {loading ? (
              <>
                <FiLoader className="animate-spin text-2xl mr-2" />
                Submitting...
              </>
            ) : (
              'Download'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
